import {ChangeDetectorRef, inject, Injectable} from '@angular/core';
import {Router} from '@angular/router';
import {AsyncSubject, defer, EMPTY, Observable, of} from 'rxjs';
import {switchMap, tap} from 'rxjs/operators';
import {bindWith} from '../../../common/rxjs/bind-with';
import {toNever} from '../../../common/rxjs/to-never';
import {FloatCellDefinition} from '../../../components/float-layout';
import {Widget} from '../../../components/layout/widget/widget';
import {WidgetDefinition} from '../../../components/layout/widget/widget-definition';
import {ModalService} from '../../../components/modal';
import {
    getOrderQtyAssetType,
    getOrderQtyCorrectedSecurityFormats,
    getOrderQtyMultiCurrencyInfoForOrder,
    getQtyAndQtyAssetTypeFromOrder
} from '../../../components/order-qty-multi-currency';
import {AlertFn} from '../../../components/response-errors/response-error-code-msg';
import {getOrderAspreaderAlgoParamsFormFields} from '../../../components/security/aspreader-security';
import {isSmartSecurityKey} from '../../../components/smart-security';
import {
    DisplayDecimalWithCurrency,
    formatPrice,
    formatQuantity,
    SymbolFormats
} from '../../../components/symbol-decimals';
import {AssetType, getSecurityAssetByType} from '../../../enums/asset-type';
import {parseAlgoTypeFromOrder} from '../../../enums/order-algo-type';
import {OrdType} from '../../../enums/order-type';
import {NewOrder} from '../../../models/order';
import {formatSecurityKey, SecurityKey} from '../../../models/security-key';
import {FloatLayoutService} from '../../../pages/dashboard/float-layout/float-layout.service';
import {TradeWidgetSettingsDefinition} from '../../trade-widget/settings';
import {TRADE_WIDGET_TYPE_ID} from '../../trade-widget/trade-widget-type-id';
import {showWidgetInnerAlert} from '../widget-inner-alert/utils';
import {
    OrderConfirmationParams,
    OrderCreationParams,
    OrderPlacingBaseService,
    PositionClosingParams
} from './order-placing-base.service';
import {getAccountBaseByNewOrderParams} from './order-placing-utils';
import {WidgetOrderAspreaderAlgoParamsAlertComponent} from './widget-order-aspreader-algo-params-alert';
import {AspreaderAlgoParamsError} from './widget-order-aspreader-algo-params-alert/aspreader-algo-params-errors';
import {WidgetOrderCommonAlertComponent} from './widget-order-common-alert';
import {WidgetOrderConfirmModalComponent} from './widget-order-confirm-modal';
import {
    CREATE_ACCOUNT_CONFIRMATION_TITLE,
    EXCHANGE_SUPPORT_CONFIRMATION_TITLE,
    ORDER_CONFIRMATION_TITLE_BY_TRANSACT_TYPE,
    SELECT_PORTFOLIO_CONFIRMATION_TITLE,
    WidgetOrderConfirmModalInfo
} from './widget-order-confirm-modal/widget-order-confirm-modal-common';
import {WidgetOrderMaxPriceAlertComponent} from './widget-order-max-price-alert';
import {WidgetOrderMaxQuantityAlertComponent} from './widget-order-max-quantity-alert';
import {WidgetOrderMinNotionalAlertComponent} from './widget-order-min-notional-alert';
import {WidgetOrderMinPriceAlertComponent} from './widget-order-min-price-alert';
import {WidgetOrderMinQuantityAlertComponent} from './widget-order-min-quantity-alert';
import {WidgetOrderStepAlertComponent} from './widget-order-step-alert';
import {WidgetOrderTwapAlgoParamsAlertComponent} from './widget-order-twap-algo-params-alert';
import {TwapAlgoParamsError} from './widget-order-twap-algo-params-alert/twap-algo-params-errors';
import {WidgetPosCloseConfirmModalComponent} from './widget-pos-close-confirm-modal';
import {PosClosingConfirmedParams} from './widget-pos-close-confirm-modal/pos-closing-confirmed-params';
import NumberFormat = Intl.NumberFormat;

interface TradeWidgetCreationParams {
    securityKey: SecurityKey;
}

@Injectable()
export class OrderPlacingService extends OrderPlacingBaseService {

    private readonly floatLayoutService = inject(FloatLayoutService, {optional: true});
    private readonly modalService = inject(ModalService);
    private readonly router = inject(Router);

    protected override initiateTradeUiCreation(params: OrderCreationParams, newOrder: NewOrder): Observable<never> {
        const {security} = params;
        const resolvedOrder$ = this.resolveOrderParams(params, newOrder);

        return resolvedOrder$.pipe(
            switchMap(resolvedOrder => {

                /**
                 * Regardless of {@link OrderQtyMultiCurrencyInfo.isMultiCurrencyNativelySupported},
                 * we need to read field {@link Order.orderQuoteQty} in order to pass it as is to Trade Ticket.
                 */
                const BYPASS_NATIVE_MULT_CUR_SUPPORT = true;

                const {qty, qtyAssetType} = getQtyAndQtyAssetTypeFromOrder(
                    security,
                    resolvedOrder,
                    undefined,
                    BYPASS_NATIVE_MULT_CUR_SUPPORT,
                    params.orderFormValue && params.orderFormValue.qtyAssetType
                );

                this.createTradeWidget(
                    {
                        securityKey: security
                    },
                    {
                        algoType:               parseAlgoTypeFromOrder(resolvedOrder.algoType),
                        account:                getAccountBaseByNewOrderParams(resolvedOrder),
                        securityKeyStr:         formatSecurityKey(security),
                        orderType:              resolvedOrder.ordType,
                        quantity:               qty,
                        qtyAssetType,
                        limitPrice:             resolvedOrder.price,
                        stopPrice:              resolvedOrder.stopPx || resolvedOrder.price,
                        side:                   resolvedOrder.side,
                        allOrNone:              resolvedOrder.allOrNone,
                        timeInForce:            resolvedOrder.timeInForce,
                        isConfirmationDisabled: params.isConfirmationDisabled,
                        investmentEntityId:     resolvedOrder.investmentEntityId,
                        portfolioId:            resolvedOrder.portfolioId,
                        leverageBySide:         {[resolvedOrder.side]: resolvedOrder.leverage},

                        ...getOrderAspreaderAlgoParamsFormFields(resolvedOrder, this.accountService)
                    }
                );

                return EMPTY;
            })
        );
    }

    protected override confirmOrderCreation(
        params: OrderConfirmationParams,
        newOrder: NewOrder
    ): Observable<NewOrder> {
        return new Observable<NewOrder>(subscriber => {
            const {security} = params;
            const qtyMultiCurrencyInfo = getOrderQtyMultiCurrencyInfoForOrder(newOrder, security);
            const qtyAssetType = getOrderQtyAssetType(security, newOrder, qtyMultiCurrencyInfo);

            const symbolFormats: SymbolFormats = getOrderQtyCorrectedSecurityFormats({
                security,
                qtyMultiCurrencyInfo,
                securityInputParams:   this.symbolDecimalsService.getSecurityInputParams(security),
                normalSecurityFormats: this.symbolDecimalsService.getSecurityFormats(security),
                toNativeQtyAssetType:  true,
                qtyAssetType
            });
            const priceFormatter = (value: number) => formatPrice(value, symbolFormats);
            const quantityFormatter = (value: number): DisplayDecimalWithCurrency => ({
                ...formatQuantity(value, symbolFormats),
                currencySymbol: (qtyAssetType === AssetType.QUOTE)
                                    ? getSecurityAssetByType(security, qtyAssetType).symbol
                                    : undefined
            });

            const account = newOrder.accountId && this.accountService.getAccountByIdFromCache(newOrder.accountId);
            const accountNames = account ? [account.displayName] : undefined;

            const widgetOrderConfirmModalInfo: WidgetOrderConfirmModalInfo = {
                title:    ORDER_CONFIRMATION_TITLE_BY_TRANSACT_TYPE[newOrder.transactionType],
                side:     newOrder.side,
                quantity: quantityFormatter(newOrder.orderQty),
                price:    priceFormatter(newOrder.price),
                security: params.security,
                accountNames
            };

            if (newOrder.ordType === OrdType.MARKET) {

                // Allow the confirmation component to subscribe to quotes and display the best price.
                widgetOrderConfirmModalInfo.isListenToQuotesEnabled = true;
            }

            const modalRef = this.modalService.open(WidgetOrderConfirmModalComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    widgetOrderConfirmModalInfo,
                    onConfirmCallback: () => subscriber.next(newOrder)
                },
                onDestroy: () => subscriber.complete()
            });
            modalRef.injector.get(ChangeDetectorRef).markForCheck();

            return () => modalRef.destroy();
        });
    }

    protected override showMinNotionalAlert(params: OrderConfirmationParams, isIcebergAlert: boolean = false): Observable<never> {
        const TITLE = 'components.widgets.trade-widget.min_total_error_title';

        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderMinNotionalAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    title:    TITLE,
                    security: params.security,
                    isIcebergAlert
                },
                onDestroy: () => subscriber.complete()
            }).injector.get(ChangeDetectorRef).markForCheck();
        });
    }

    protected override showMinQuantityAlert(params: OrderConfirmationParams, newOrder: NewOrder): Observable<never> {
        const TITLE = 'components.widgets.trade-widget.min_quantity_error_title';

        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderMinQuantityAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    title:    TITLE,
                    security: params.security,
                    order:    newOrder
                },
                onDestroy: () => subscriber.complete()
            }).injector.get(ChangeDetectorRef).markForCheck();
        });
    }

    protected override showMaxQuantityAlert(params: OrderConfirmationParams, newOrder: NewOrder): Observable<never> {
        const TITLE = 'components.widgets.trade-widget.max_quantity_error_title';

        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderMaxQuantityAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    title:    TITLE,
                    security: params.security,
                    order:    newOrder
                },
                onDestroy: () => subscriber.complete()
            }).injector.get(ChangeDetectorRef).markForCheck();
        });
    }

    protected override showMinPriceAlert(params: OrderConfirmationParams): Observable<never> {
        const TITLE = 'components.widgets.trade-widget.min_price_error_title';

        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderMinPriceAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    title:    TITLE,
                    security: params.security
                },
                onDestroy: () => subscriber.complete()
            }).injector.get(ChangeDetectorRef).markForCheck();
        });
    }

    protected override showMaxPriceAlert(params: OrderConfirmationParams): Observable<never> {
        const TITLE = 'components.widgets.trade-widget.max_price_error_title';

        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderMaxPriceAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    title:    TITLE,
                    security: params.security
                },
                onDestroy: () => subscriber.complete()
            }).injector.get(ChangeDetectorRef).markForCheck();
        });
    }

    protected override showStepAlert({params, title, message, step, format}: {
        params: OrderConfirmationParams,
        title: string,
        message: string,
        step: number,
        format: NumberFormat
    }): Observable<never> {
        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderStepAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    title,
                    step,
                    format,
                    message
                },
                onDestroy: () => subscriber.complete()
            });
        });
    }

    protected override showCommonOrderValidationAlert(params: OrderConfirmationParams): Observable<never> {
        const TITLE = 'components.widgets.trade-widget.common_error_title';

        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderCommonAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    title:    TITLE
                },
                onDestroy: () => subscriber.complete()
            }).injector.get(ChangeDetectorRef).markForCheck();
        });
    }

    protected override showTwapAlgoParamsAlert(
        params: OrderConfirmationParams,
        twapAlgoParamsErrors: TwapAlgoParamsError[]
    ): Observable<never> {
        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderTwapAlgoParamsAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    twapAlgoParamsErrors
                },
                onDestroy: () => subscriber.complete()
            }).injector.get(ChangeDetectorRef).markForCheck();
        });
    }

    protected override showAspreaderAlgoParamsAlert(
        params: OrderConfirmationParams,
        aspreaderAlgoParamsErrors: AspreaderAlgoParamsError[]
    ): Observable<never> {
        return new Observable(subscriber => {
            this.modalService.open(WidgetOrderAspreaderAlgoParamsAlertComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    aspreaderAlgoParamsErrors
                },
                onDestroy: () => subscriber.complete()
            }).injector.get(ChangeDetectorRef).markForCheck();
        });
    }

    protected override confirmExchangeSupport(params: OrderConfirmationParams): Observable<never> {
        const widgetOrderConfirmModalInfo: WidgetOrderConfirmModalInfo = {
            title: EXCHANGE_SUPPORT_CONFIRMATION_TITLE,
            isExchangeNotAllowedToTrade: true,
            security: params.security
        };

        this.modalService.open(WidgetOrderConfirmModalComponent, {
            injector:  this.injector,
            place:     {
                viewContainerRef: params.confirmModalAnchorVcRef
            },
            configure: {
                isWide: true,
                widgetOrderConfirmModalInfo,
                onConfirmCallback: () => null
            }
        }).injector.get(ChangeDetectorRef).markForCheck();

        return EMPTY;
    }

    protected override confirmAccountCreation(
        params: OrderConfirmationParams
    ): Observable<never> {
        const confirmation$ = new Observable<void>(subscriber => {
            const widgetOrderConfirmModalInfo: WidgetOrderConfirmModalInfo = {
                title: CREATE_ACCOUNT_CONFIRMATION_TITLE,
                isNeedToHaveAnAccount: true,
                security: params.security
            };

            this.modalService.open(WidgetOrderConfirmModalComponent, {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                configure: {
                    isWide: true,
                    widgetOrderConfirmModalInfo,
                    onConfirmCallback: () => subscriber.next()
                }
            }).injector.get(ChangeDetectorRef).markForCheck();
        });

        const exchange$ = defer(() =>
            isSmartSecurityKey(params.security)
                ? this.smartSecurityService.getSmartSecurityAccountCreationExchange(params.security)
                : of(params.security.securityExchange)
        );

        return confirmation$.pipe(
            switchMap(() => exchange$),
            tap(exchange => this.router.navigate(
                ['/accounts', 'create'],
                {queryParams: {exchange}}
            )),
            toNever()
        );
    }

    protected override confirmSelectPortfolioId(params: OrderConfirmationParams): Observable<never> {
        const widgetOrderConfirmModalInfo: WidgetOrderConfirmModalInfo = {
            title: SELECT_PORTFOLIO_CONFIRMATION_TITLE,
            isPortfolioNeeded: true,
            security: params.security
        };

        this.modalService.open(WidgetOrderConfirmModalComponent, {
            injector:  this.injector,
            place:     {
                viewContainerRef: params.confirmModalAnchorVcRef
            },
            configure: {
                isWide: true,
                widgetOrderConfirmModalInfo,
                onConfirmCallback: () => null
            }
        }).injector.get(ChangeDetectorRef).markForCheck();

        return EMPTY;
    }

    protected override confirmPositionClosing(
        params: PositionClosingParams
    ): Observable<PosClosingConfirmedParams | never> {
        const {position} = params;
        const resultSubject$ = new AsyncSubject<PosClosingConfirmedParams>();

        const modalRef = this.modalService.open<WidgetPosCloseConfirmModalComponent>(
            WidgetPosCloseConfirmModalComponent,
            {
                injector:  this.injector,
                place:     {
                    viewContainerRef: params.confirmModalAnchorVcRef
                },
                onDestroy: () => resultSubject$.complete(),
                configure: {
                    position,

                    onPosCloseConfirmCallback: args =>
                        resultSubject$.next(args)
                }
            }
        );

        return resultSubject$.asObservable().pipe(
            bindWith(modalRef.asObservable())
        );
    }

    protected override getErrorAlertFn(): AlertFn {
        return showWidgetInnerAlert;
    }

    private createTradeWidget(
        params: TradeWidgetCreationParams,
        tradeWidgetParams: Partial<TradeWidgetSettingsDefinition>
    ): void {
        const initiatorWidget = this.injector.get(Widget);

        tradeWidgetParams = {
            ...tradeWidgetParams,
            title:          null,
            securityKeyStr: tradeWidgetParams.securityKeyStr || formatSecurityKey(params.securityKey)
        };

        const floatCellDef: FloatCellDefinition = {
            id:                null,
            pos:               [null, null],
            size:              [null, null],
            activeWidgetIndex: 0,
            widgets:           []
        };
        const widgetDef: WidgetDefinition = {
            id:           null,
            tabId:        initiatorWidget.tabId,
            cellId:       null,
            widgetTypeId: TRADE_WIDGET_TYPE_ID,
            settings:     tradeWidgetParams as TradeWidgetSettingsDefinition
        };

        this.floatLayoutService.createFloatCellByDefinition(floatCellDef, widgetDef, initiatorWidget.cell);
    }

}
